import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { Mail, Lock, ArrowRight, Eye, EyeOff, Clock, XCircle } from 'lucide-react';

const Login = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [pendingMessage, setPendingMessage] = useState(null);
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setPendingMessage(null);
    try {
      const userData = await login(email, password);
      toast.success('Connexion réussie !');
      if (userData?.role === 'admin') {
        navigate('/admin');
      } else {
        navigate('/dashboard');
      }
    } catch (error) {
      const status = error.response?.status;
      const msg = error.response?.data?.detail || 'Erreur de connexion';
      if (status === 403) {
        setPendingMessage(msg);
      } else {
        toast.error(msg);
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex" data-testid="login-page">
      {/* Left panel - branding */}
      <div className="hidden lg:flex lg:w-[45%] relative bg-gradient-to-br from-green-600 to-green-800 items-center justify-center p-12">
        <div className="absolute inset-0 opacity-[0.04]" style={{ backgroundImage: 'radial-gradient(circle at 1px 1px, white 1px, transparent 0)', backgroundSize: '32px 32px' }} />
        <div className="relative z-10 text-white max-w-md">
          <div className="flex items-center gap-3 mb-8">
            <img src="/logo.png" alt="Green Teva" className="w-12 h-12 rounded-xl" />
            <span className="text-2xl font-bold">Green Teva</span>
          </div>
          <h2 className="text-3xl font-bold mb-4 leading-tight">
            L'énergie renouvelable, partagée entre voisins
          </h2>
          <p className="text-green-100 text-base leading-relaxed">
            Rejoignez une coopérative énergétique et profitez d'une électricité locale, verte et économique.
          </p>
          <div className="mt-10 grid grid-cols-3 gap-6">
            <div>
              <p className="text-2xl font-bold">-30%</p>
              <p className="text-xs text-green-200 mt-1">sur la facture</p>
            </div>
            <div>
              <p className="text-2xl font-bold">100%</p>
              <p className="text-xs text-green-200 mt-1">énergie locale</p>
            </div>
            <div>
              <p className="text-2xl font-bold">10+</p>
              <p className="text-xs text-green-200 mt-1">communautés</p>
            </div>
          </div>
        </div>
      </div>

      {/* Right panel - form */}
      <div className="flex-1 flex items-center justify-center p-6 sm:p-8 bg-[#f8fafc]">
        <div className="w-full max-w-md">
          {/* Mobile logo */}
          <div className="flex lg:hidden items-center gap-2 mb-8">
            <img src="/logo.png" alt="Green Teva" className="w-9 h-9 rounded-xl" />
            <span className="text-lg font-bold text-green-700">Green Teva</span>
          </div>

          <div className="mb-8">
            <h1 className="text-2xl sm:text-3xl font-bold text-foreground tracking-tight">
              Connexion
            </h1>
            <p className="text-muted-foreground text-sm mt-2">
              Accédez à votre espace de coopérative énergétique
            </p>
          </div>

          <Card className="border border-green-100/60 shadow-sm">
            <CardContent className="p-6 sm:p-8">
              {pendingMessage && (
                <div className={`mb-5 p-4 rounded-xl border ${
                  pendingMessage.includes('refusée')
                    ? 'bg-red-50 border-red-200'
                    : 'bg-amber-50 border-amber-200'
                }`} data-testid="pending-message">
                  <div className="flex items-start gap-3">
                    {pendingMessage.includes('refusée') ? (
                      <XCircle className="w-5 h-5 text-red-500 shrink-0 mt-0.5" />
                    ) : (
                      <Clock className="w-5 h-5 text-amber-500 shrink-0 mt-0.5" />
                    )}
                    <p className={`text-sm leading-relaxed ${
                      pendingMessage.includes('refusée') ? 'text-red-700' : 'text-amber-700'
                    }`}>
                      {pendingMessage}
                    </p>
                  </div>
                </div>
              )}
              <form onSubmit={handleSubmit} className="space-y-5" data-testid="login-form">
                <div className="space-y-2">
                  <Label htmlFor="email" className="text-sm font-medium">Adresse email</Label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                    <Input
                      id="email"
                      type="email"
                      placeholder="vous@exemple.com"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className="pl-10 h-11 bg-white border-green-200/80 focus:border-green-500 focus:ring-green-500/10 rounded-xl"
                      required
                      data-testid="login-email-input"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <Label htmlFor="password" className="text-sm font-medium">Mot de passe</Label>
                    <Link to="/request-password-reset" className="text-xs text-green-600 hover:text-green-700 font-medium" data-testid="forgot-password-link">
                      Mot de passe oublié ?
                    </Link>
                  </div>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                    <Input
                      id="password"
                      type={showPassword ? 'text' : 'password'}
                      placeholder="Votre mot de passe"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      className="pl-10 pr-10 h-11 bg-white border-green-200/80 focus:border-green-500 focus:ring-green-500/10 rounded-xl"
                      required
                      data-testid="login-password-input"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                    >
                      {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>

                <Button
                  type="submit"
                  className="w-full btn-primary h-11 text-base"
                  disabled={loading}
                  data-testid="login-submit-btn"
                >
                  {loading ? (
                    <div className="loader !w-5 !h-5 !border-white/30 !border-t-white" />
                  ) : (
                    <>
                      Se connecter
                      <ArrowRight className="ml-2 w-4 h-4" />
                    </>
                  )}
                </Button>
              </form>
            </CardContent>
          </Card>

          <p className="text-center text-sm text-muted-foreground mt-6">
            Pas encore de compte ?{' '}
            <Link to="/register" className="text-green-600 hover:text-green-700 font-semibold" data-testid="register-link">
              Créer un compte
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
};

export default Login;
